import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import { updateClientMatchStatus } from "../api/matches";
import { messageFromError } from "../../../shared/api/drfErrors";
import {
  formatMatchPrice,
  formatMatchScorePercent,
  propertyTypeLabel,
  statusLabel,
} from "./ClientMatchRow";

const STATUS_OPTIONS = [
  { value: "new", label: "Новий" },
  { value: "contacted", label: "Зв\u2019язалися" },
  { value: "viewing", label: "Перегляд заплановано" },
  { value: "rejected", label: "Відхилено" },
];

const DEAL_TYPE_LABELS = {
  sale: "Продаж",
  rent: "Оренда",
};

const formatLocation = (city, district) => {
  const parts = [];
  if (typeof city === "string" && city.trim()) parts.push(city.trim());
  if (typeof district === "string" && district.trim()) {
    parts.push(district.trim());
  }
  return parts.length > 0 ? parts.join(", ") : null;
};

const dealTypeLabel = (dealType) => {
  if (!dealType) return null;
  return DEAL_TYPE_LABELS[dealType] || dealType;
};

function ClientMatchCard({ match, clientId, canEdit, onUpdated }) {
  const [draftStatus, setDraftStatus] = useState(match.status || "new");
  const [submitting, setSubmitting] = useState(false);
  const [rowError, setRowError] = useState(null);
  const submittingRef = useRef(false);

  useEffect(() => {
    setDraftStatus(match.status || "new");
  }, [match.status]);

  const confirmedStatus = match.status || "new";
  const statusKey = confirmedStatus;
  const statusText = statusLabel(statusKey);
  const propertyId = match.property_id;
  const propertyTitle =
    match.property_title || `Об'єкт #${propertyId || "—"}`;
  const scoreText = formatMatchScorePercent(match.score);
  const priceText = formatMatchPrice(
    match.property_price,
    match.property_currency,
  );
  const typeText = propertyTypeLabel(match.property_type);
  const dealText = dealTypeLabel(match.property_deal_type);
  const locationText = formatLocation(
    match.property_city,
    match.property_district,
  );
  const previewImage = match.property_preview_image || null;
  const unchanged = draftStatus === confirmedStatus;

  const handleSave = async () => {
    if (unchanged || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setRowError(null);
    try {
      const updated = await updateClientMatchStatus(
        clientId,
        match.id,
        draftStatus,
      );
      onUpdated(updated);
      setDraftStatus(updated.status || draftStatus);
      toast.success("Статус підбору оновлено.");
    } catch (err) {
      setRowError(messageFromError(err));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <article
      className="feature-client-detail__match-card"
      data-testid="match-card"
    >
      <div className="feature-client-detail__match-card-media">
        {previewImage ? (
          <img
            src={previewImage}
            alt=""
            className="feature-client-detail__match-card-image"
          />
        ) : (
          <div
            className="feature-client-detail__match-card-fallback"
            aria-hidden="true"
          >
            <i className="fa-solid fa-house" />
          </div>
        )}
        <span
          className={`feature-client-detail__match-status feature-client-detail__match-confirmed-status feature-client-detail__match-status--${statusKey}`}
          data-testid="match-confirmed-status"
        >
          {statusText}
        </span>
      </div>

      <div className="feature-client-detail__match-card-body">
        <div className="feature-client-detail__match-card-head">
          {propertyId ? (
            <Link
              to={`/realty/${propertyId}`}
              className="feature-client-detail__match-property"
            >
              {propertyTitle}
            </Link>
          ) : (
            <span className="feature-client-detail__match-property">
              {propertyTitle}
            </span>
          )}
          <span className="feature-client-detail__match-score">{scoreText}</span>
        </div>

        {locationText ? (
          <p className="feature-client-detail__match-card-location">
            <i className="fa-solid fa-location-dot" aria-hidden="true" />
            <span>{locationText}</span>
          </p>
        ) : null}

        <div className="feature-client-detail__match-meta">
          <span className="feature-client-detail__match-price">{priceText}</span>
          {dealText ? (
            <span className="feature-client-detail__match-deal">{dealText}</span>
          ) : null}
          <span className="feature-client-detail__match-type">{typeText}</span>
        </div>

        {canEdit ? (
          <div className="feature-client-detail__match-controls">
            <label className="feature-client-detail__match-status-label">
              <span className="feature-client-detail__match-status-label-text">
                Новий статус
              </span>
              <select
                className="feature-client-detail__match-status-select"
                value={draftStatus}
                onChange={(e) => setDraftStatus(e.target.value)}
                disabled={submitting}
                aria-label={`Статус підбору для ${propertyTitle}`}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="feature-client-detail__match-save-btn"
              onClick={handleSave}
              disabled={unchanged || submitting}
              data-loading={submitting ? "true" : undefined}
            >
              {submitting ? "Збереження…" : "Зберегти"}
            </button>
          </div>
        ) : null}

        {rowError ? (
          <div
            className="feature-client-detail__match-row-error"
            role="alert"
          >
            {rowError}
          </div>
        ) : null}
      </div>
    </article>
  );
}

export default ClientMatchCard;
